/**
 * Ort im Customer-Portal, auf den ein „Go to"-Ziel zeigt.
 * Pfad, Query und Hash werden unverändert aus der Eingabe übernommen, damit
 * beliebige Unterseiten eines Agents (Reporting, Versionen, Test, QA, Teststudio,
 * einzelne Conversation …) ohne Sonderfall pro Route funktionieren.
 */
export interface PortalLocation {
  /**
   * Ziel-Pfad im Customer-Portal ab dem erkannten Bereichs-Segment, mit führendem
   * Slash und ohne Locale-Präfix (z.B. `/voicebots/<id>/conversations/<id>`).
   */
  path: string;
  /** Query-String der Eingabe inkl. führendem `?`; leerer String, wenn keiner vorhanden war. */
  search: string;
  /** Fragment der Eingabe inkl. führendem `#`; leerer String, wenn keines vorhanden war. */
  hash: string;
  /**
   * Origin (Schema + Host + Port) der Eingabe, falls es eine absolute http(s)-URL war;
   * sonst `null`. Erlaubt es, den Ziel-Tab auf demselben Host zu öffnen, von dem der
   * Link stammt (z.B. localhost beim lokalen Testen).
   */
  origin: string | null;
}

/** Link auf einen Agent oder eine seiner Unterseiten. Der Customer wird über die Agent-ID aufgelöst. */
export interface AgentLinkTarget extends PortalLocation {
  kind: 'agent-link';
  /** Bereichs-Segment des Links — bestimmt die Ziel-Route ('voicebots' | 'chatbots'). */
  botType: 'voicebots' | 'chatbots';
  /** Agent-/Bot-UUID aus dem Link. */
  agentId: string;
  /**
   * ID der Conversation, wenn der Link direkt auf `…/conversations/<id>` zeigt; sonst `null`.
   * Typisierte Quelle für die Unterscheidung „Chat geöffnet" vs. „Agent geöffnet".
   */
  conversationId: string | null;
}

/** Link in den Knowledge-Bereich. Der Customer wird über die Verzeichnis-ID aufgelöst. */
export interface KnowledgeLinkTarget extends PortalLocation {
  kind: 'knowledge-link';
  /** UUID des Knowledge-Verzeichnisses aus dem Link. */
  knowledgeDirectoryId: string;
}

/**
 * Eingegebene Rufnummer. Der Ziel-Pfad steht erst fest, nachdem der Backend-Lookup
 * die Nummer auf einen Agent aufgelöst hat.
 */
export interface PhoneNumberTarget {
  kind: 'phone-number';
  /** Rufnummer in der Schreibweise, wie sie eingegeben wurde (getrimmt). */
  phoneNumber: string;
}

/** Alle Eingabeformen, die das „Go to"-Feld auflösen kann. */
export type GotoTarget = AgentLinkTarget | KnowledgeLinkTarget | PhoneNumberTarget;

/** UUID-Form (8-4-4-4-12 Hex), case-insensitive. */
const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';

/**
 * Beliebig viele weitere Pfad-Segmente hinter der ID — sie werden mitgenommen, damit
 * jede Agent-Unterseite (`/reporting`, `/versions`, `/conversations/<id>`, …) trifft.
 */
const TRAILING_SEGMENTS = '((?:/[^/]+)*)';

/** Matcht `/<botType>/<agentId>` samt allen Folge-Segmenten; ein Locale-Präfix davor stört nicht. */
const AGENT_PATH_REGEX = new RegExp(`/(voicebots|chatbots)/(${UUID})${TRAILING_SEGMENTS}`, 'i');

/** Matcht `/knowledge/<directoryId>` samt allen Folge-Segmenten (Dokumente, Chunks, Suche, Edit). */
const KNOWLEDGE_PATH_REGEX = new RegExp(`/knowledge/(${UUID})${TRAILING_SEGMENTS}`, 'i');

/** Matcht ein direkt auf die Agent-ID folgendes `/conversations/<id>`-Segment. */
const CONVERSATION_SEGMENT_REGEX = /^\/conversations\/([^/]+)/i;

/**
 * Eingabe, die ausschließlich aus Ziffern und üblichen Rufnummern-Trennzeichen besteht
 * (optional mit führendem `+`). Grenzt Rufnummern von Links ab, bevor URL-Parsing greift.
 */
const PHONE_INPUT_REGEX = /^\+?[\d\s()./-]*\d[\d\s()./-]*$/;

/**
 * Mindestanzahl signifikanter Ziffern, ab der eine Eingabe als Rufnummer gilt.
 * Muss zu `MIN_PHONE_LOOKUP_DIGITS` im agent-management-Service passen — gleiche Zahl
 * UND gleiche Bezugsgröße: gezählt wird wie dort nach Abzug der Amtskennzahl `00`
 * bzw. der Verkehrsausscheidungsziffer `0` (siehe {@link countSignificantPhoneDigits}).
 */
const MIN_PHONE_DIGITS = 6;

/**
 * Zählt die signifikanten Ziffern einer Rufnummern-Eingabe wie die Backend-Normalisierung
 * (`normalizePhoneNumber` in agent-management): Trennzeichen entfallen, ein führendes
 * `00` bzw. `0` zählt nicht mit. So lehnt das Feld genau die Eingaben ab, die auch das
 * Backend mit „fewer than 6 significant digits" ablehnen würde.
 * @param input Bereits getrimmte Roh-Eingabe
 */
function countSignificantPhoneDigits(input: string): number {
  const digits = input.replace(/\D/g, '');
  if (digits.startsWith('00')) {
    return digits.length - 2;
  }
  if (digits.startsWith('0')) {
    return digits.length - 1;
  }
  return digits.length;
}

/** Basis für das Parsen relativer Eingaben; taucht nie in einer Ausgabe auf. */
const RELATIVE_INPUT_BASE = 'http://portal-link.invalid';

/**
 * Zerlegt die Eingabe in eine URL. Absolute http(s)-Eingaben behalten ihren Origin,
 * alles andere wird relativ zu einer Platzhalter-Basis geparst (Origin `null`).
 * @param input Bereits getrimmte Roh-Eingabe
 * @returns URL samt Origin oder `null`, wenn die Eingabe nicht als URL lesbar ist
 */
function toLocationUrl(input: string): { url: URL; origin: string | null } | null {
  try {
    const absolute = new URL(input);
    if (absolute.protocol === 'http:' || absolute.protocol === 'https:') {
      return { url: absolute, origin: absolute.origin };
    }
    // Andere Schemata (mailto:, javascript:, …) sind keine Portal-Links.
    return null;
  } catch {
    // Keine absolute URL — unten als relativer Pfad versuchen.
  }

  try {
    return { url: new URL(input, RELATIVE_INPUT_BASE), origin: null };
  } catch {
    return null;
  }
}

/**
 * Prüft, ob die Eingabe eine Rufnummer ist (nur Ziffern und Trennzeichen,
 * mindestens {@link MIN_PHONE_DIGITS} Ziffern).
 * @param input Bereits getrimmte Roh-Eingabe
 */
function isPhoneNumberInput(input: string): boolean {
  if (!PHONE_INPUT_REGEX.test(input)) {
    return false;
  }
  return countSignificantPhoneDigits(input) >= MIN_PHONE_DIGITS;
}

/**
 * Erkennt, worauf eine „Go to"-Eingabe zeigt: eine Rufnummer, einen Agent-Link
 * (inklusive aller Unterseiten) oder einen Knowledge-Link. Akzeptiert volle URLs
 * ebenso wie reine Pfade; Whitespace wird getrimmt, ein Locale-Präfix ignoriert.
 * @param input Roh-Eingabe aus dem Eingabefeld
 * @returns Erkanntes Ziel oder `null`, wenn die Eingabe keiner bekannten Form entspricht
 */
export function parseGotoInput(input: string): GotoTarget | null {
  const trimmed = input.trim();
  if (!trimmed) {
    return null;
  }

  if (isPhoneNumberInput(trimmed)) {
    return { kind: 'phone-number', phoneNumber: trimmed };
  }

  const parsed = toLocationUrl(trimmed);
  if (!parsed) {
    return null;
  }
  const { url, origin } = parsed;
  const location = { search: url.search, hash: url.hash, origin };

  const agentMatch = AGENT_PATH_REGEX.exec(url.pathname);
  if (agentMatch) {
    const botType = agentMatch[1].toLowerCase() as AgentLinkTarget['botType'];
    const agentId = agentMatch[2];
    const conversationMatch = CONVERSATION_SEGMENT_REGEX.exec(agentMatch[3]);
    return {
      kind: 'agent-link',
      botType,
      agentId,
      conversationId: conversationMatch ? conversationMatch[1] : null,
      path: `/${botType}/${agentId}${agentMatch[3]}`,
      ...location,
    };
  }

  const knowledgeMatch = KNOWLEDGE_PATH_REGEX.exec(url.pathname);
  if (knowledgeMatch) {
    const knowledgeDirectoryId = knowledgeMatch[1];
    return {
      kind: 'knowledge-link',
      knowledgeDirectoryId,
      path: `/knowledge/${knowledgeDirectoryId}${knowledgeMatch[2]}`,
      ...location,
    };
  }

  return null;
}

/** Agent-Typ, wie ihn das `AgentType`-Backend-Enum kennt — bestimmt die Portal-Route. */
export type PortalAgentType = 'voice' | 'chat';

/**
 * Verengt den `type`-String aus dem Admin-Lookup auf {@link PortalAgentType}.
 * Unbekannte Werte werden hart abgewiesen statt still auf `chatbots` zu raten —
 * ein künftiger dritter Agent-Typ fällt so beim Aufrufer auf, nicht als 404 beim Nutzer.
 * @param agentType Roh-Wert aus dem API-Response (System-Grenze)
 * @throws Error wenn der Wert kein bekannter Agent-Typ ist
 */
export function toPortalAgentType(agentType: string): PortalAgentType {
  const normalized = agentType.toLowerCase();
  if (normalized === 'voice' || normalized === 'chat') {
    return normalized;
  }
  throw new Error(`Unbekannter Agent-Typ "${agentType}" — Ziel-Route kann nicht bestimmt werden.`);
}

/**
 * Baut den Ziel-Pfad für einen per Rufnummer aufgelösten Agent.
 * @param agentType Agent-Typ aus dem Admin-Lookup (über {@link toPortalAgentType} verengt)
 * @param agentId Agent-UUID
 * @returns Pfad zur Agent-Detailseite im Customer-Portal
 */
export function buildAgentDetailPath(agentType: PortalAgentType, agentId: string): string {
  const botType = agentType === 'voice' ? 'voicebots' : 'chatbots';
  return `/${botType}/${agentId}`;
}

/**
 * Setzt die vollständige Ziel-URL zusammen: Host, Pfad, ursprüngliche Query samt
 * angehängtem Impersonation-Token und das ursprüngliche Fragment.
 * @param baseUrl Host-Basis ohne Trailing-Slash
 * @param path Ziel-Pfad mit führendem Slash
 * @param impersonationToken Token, das auf jeder Portal-Route greift
 * @param location Optionale Query/Hash-Anteile der Eingabe, die erhalten bleiben sollen
 * @returns Absolute URL zum Öffnen in einem neuen Tab
 */
export function buildPortalTargetUrl(
  baseUrl: string,
  path: string,
  impersonationToken: string,
  location?: Pick<PortalLocation, 'search' | 'hash'>,
): string {
  const params = new URLSearchParams(location?.search ?? '');
  params.set('impersonation_token', impersonationToken);
  return `${baseUrl}${path}?${params.toString()}${location?.hash ?? ''}`;
}

/**
 * Konfigurierte Basis-URL des Customer-Portals (ohne Trailing-Slash).
 * Fallback auf den lokalen Dev-Host, wenn `VITE_CUSTOMER_PORTAL_URL` fehlt.
 */
export function getCustomerPortalBaseUrl(): string {
  const raw = import.meta.env.VITE_CUSTOMER_PORTAL_URL || 'http://app.callbert.localhost:3005/';
  return raw.replace(/\/+$/, '');
}

/**
 * Baut den Agent-Detail-Pfad im Customer-Portal aus Agent-Typ und -ID.
 * Voice-Typen (`voice`, `voicebot`) landen unter `/voicebots/…`, alles andere
 * unter `/chatbots/…`.
 * @param agentType Typ-Feld des Agents (z.B. aus {@link AdminAgentIdentity})
 */
export function buildAgentPortalPath(agentType: string, agentId: string): string {
  const botType = agentType.toLowerCase().includes('voice') ? 'voicebots' : 'chatbots';
  return `/${botType}/${agentId}`;
}
